import { Component, ErrorInfo, ReactNode } from 'react';
import { logger } from '@/lib/logger';
import { observability } from '@/lib/observability';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw, Copy } from 'lucide-react';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  level?: 'app' | 'section';
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorId: string | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorId: null };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    const errorId = `ERR-${Date.now().toString(36).toUpperCase()}`;
    return { hasError: true, error, errorId };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.error(`[ErrorBoundary] Uncaught error (${this.state.errorId}):`, error);
    logger.error('[ErrorBoundary] Component stack:', errorInfo.componentStack);

    // #90: route via vendor-neutral observability-facade FÖRE fallback-render.
    // Adaptern bakom kan vara console-logger (default) eller Sentry; här
    // skickas bara errorId + level — INGEN PII från error-meddelandet
    // separat exposas, det går genom captureException-handlern självt.
    observability.captureException(error, {
      errorId: this.state.errorId ?? undefined,
      tags: { boundaryLevel: this.props.level ?? 'section' },
    });

    this.props.onError?.(error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorId: null });
  };

  handleCopyErrorId = () => {
    if (this.state.errorId) {
      navigator.clipboard?.writeText(this.state.errorId).catch(() => {});
    }
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.props.fallback) {
      return this.props.fallback;
    }

    const isAppLevel = this.props.level === 'app';

    return (
      <div className="flex items-center justify-center min-h-[200px] w-full p-8" role="alert">
        <div className="max-w-md w-full text-center space-y-4">
          <div className="mx-auto w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center">
            <AlertTriangle className="h-6 w-6 text-destructive" />
          </div>

          <div className="space-y-2">
            <h2 className="text-lg font-semibold text-foreground">
              {isAppLevel ? 'Something went wrong' : 'This section encountered an error'}
            </h2>
            <p className="text-sm text-muted-foreground">
              {isAppLevel
                ? 'The application encountered an unexpected error. Your work has been preserved in local storage.'
                : 'An error occurred in this part of the application. You can try again or reload the page.'}
            </p>
          </div>

          {this.state.errorId && (
            <div className="flex items-center justify-center gap-2">
              <code className="text-xs bg-muted px-2 py-1 rounded font-mono text-muted-foreground">
                {this.state.errorId}
              </code>
              <button
                onClick={this.handleCopyErrorId}
                className="text-muted-foreground hover:text-foreground transition-colors"
                title="Copy error ID"
              >
                <Copy className="h-3 w-3" />
              </button>
            </div>
          )}

          <div className="flex items-center justify-center gap-3">
            {!isAppLevel && (
              <Button variant="outline" size="sm" onClick={this.handleReset}>
                Try Again
              </Button>
            )}
            <Button variant="default" size="sm" onClick={this.handleReload}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Reload Page
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
